import { Button } from "@src/components/ui/button";
import { useDrag } from "@use-gesture/react";
import { FileText } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

export interface FloatingStitchButtonRenderProps {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  id: string;
}

export interface FloatingStitchButtonProps {
  /** Corner of the viewport where the button initially appears. */
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  /** Icon rendered in the button. Any React node (e.g. a lucide icon). */
  icon?: ReactNode;
  /** Accessible label. */
  label?: string;
  /** Fired when side panel opens. */
  onOpen?: () => void;
  children: (props: FloatingStitchButtonRenderProps) => JSX.Element;
}

const STORAGE_KEY = "el8-floating-stitch-button-position";

const DEFAULT_HORIZONTAL_OFFSET = 24;
const DEFAULT_VERTICAL_OFFSET = 64;
const BUTTON_SIZE = 56;

const getDefaultPosition = (
  position: "bottom-right" | "bottom-left" | "top-right" | "top-left",
): { left?: number; right?: number; top?: number; bottom?: number } => {
  switch (position) {
    case "bottom-right":
      return {
        right: DEFAULT_HORIZONTAL_OFFSET,
        bottom: DEFAULT_VERTICAL_OFFSET,
      };
    case "bottom-left":
      return {
        left: DEFAULT_HORIZONTAL_OFFSET,
        bottom: DEFAULT_VERTICAL_OFFSET,
      };
    case "top-right":
      return { right: DEFAULT_HORIZONTAL_OFFSET, top: DEFAULT_VERTICAL_OFFSET };
    case "top-left":
      return { left: DEFAULT_HORIZONTAL_OFFSET, top: DEFAULT_VERTICAL_OFFSET };
  }
};

const convertToRelative = (
  left: number,
  top: number,
): { left?: number; right?: number; top?: number; bottom?: number } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const pos: { left?: number; right?: number; top?: number; bottom?: number } =
    {};

  if (left < viewportWidth / 2) {
    pos.left = left;
  } else {
    pos.right = viewportWidth - left - BUTTON_SIZE;
  }

  if (top < viewportHeight / 2) {
    pos.top = top;
  } else {
    pos.bottom = viewportHeight - top - BUTTON_SIZE;
  }

  return pos;
};

const convertToAbsolute = (pos: {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}): [number, number] => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const left =
    pos.left ??
    viewportWidth - (pos.right ?? DEFAULT_HORIZONTAL_OFFSET) - BUTTON_SIZE;
  const top =
    pos.top ??
    viewportHeight - (pos.bottom ?? DEFAULT_VERTICAL_OFFSET) - BUTTON_SIZE;

  return [left, top];
};

function useInitialPosition(
  position: "bottom-right" | "bottom-left" | "top-right" | "top-left",
) {
  return useState<{
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  }>(() => {
    if (typeof window === "undefined") return getDefaultPosition(position);
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : getDefaultPosition(position);
  });
}

const FloatingStitchButtonCloseContext = createContext<(() => void) | null>(
  null,
);

export const useFloatingStitchButtonClose = () => {
  const close = useContext(FloatingStitchButtonCloseContext);

  if (!close) {
    throw new Error(
      "useFloatingStitchButtonClose must be used within FloatingStitchButton",
    );
  }

  return close;
};

export const FloatingStitchButton = ({
  position = "bottom-right",
  icon,
  label = "Open Stitch",
  children,
  onOpen,
}: FloatingStitchButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useInitialPosition(position);
  const [isDragging, setIsDragging] = useState(false);

  const bind = useDrag(
    ({ offset: [x, y], first, last }) => {
      if (first) {
        setIsDragging(true);
      }
      const newPos = convertToRelative(x, y);
      setPos(newPos);
      if (last) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newPos));
        setIsDragging(false);
      }
    },
    {
      from: () => convertToAbsolute(pos),
      delay: 500,
      filterTaps: true,
    },
  );

  const handleClose = () => {
    setIsOpen(false);
  };

  const style: React.CSSProperties = {
    position: "fixed",
    left: pos.left !== undefined ? `${pos.left}px` : undefined,
    right: pos.right !== undefined ? `${pos.right}px` : undefined,
    top: pos.top !== undefined ? `${pos.top}px` : undefined,
    bottom: pos.bottom !== undefined ? `${pos.bottom}px` : undefined,
    transform: isDragging ? "scale(1.15)" : undefined,
    touchAction: "none",
    zIndex: 40,
  };

  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    }
  }, [isOpen, onOpen]);

  return (
    <FloatingStitchButtonCloseContext.Provider value={handleClose}>
      <Button
        aria-label={label}
        data-position={position}
        data-dragging={isDragging}
        size="icon"
        className="size-14 rounded-full bg-accent text-accent-foreground shadow-elevated hover:bg-accent/90 [&_svg]:size-6"
        style={style}
        onClick={() => setIsOpen(true)}
        {...bind()}
      >
        {icon ?? <FileText />}
      </Button>
      {children({
        isOpen,
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        id: "stitch",
      })}
    </FloatingStitchButtonCloseContext.Provider>
  );
};
